/**
 * SVG filter definitions for Color Vision Deficiency (CVD) simulation.
 *
 * Renders a hidden SVG containing feColorMatrix filters for each CVD type.
 * Other components apply these via CSS `filter: url(#filter-id)`.
 *
 * Matrices based on Machado et al. 2009 (severity 1.0) for
 * protanopia / deuteranopia / tritanopia, and standard luminance
 * weighting for achromatopsia.
 *
 * @module components/accessibility/CVDFilterOverlay
 */
import React from 'react';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

export type CvdFilterMode =
  | 'none'
  | 'protanopia'
  | 'deuteranopia'
  | 'tritanopia'
  | 'achromatopsia';

/**
 * Map of CVD filter mode to the CSS `filter` url() reference.
 * Use `CVD_FILTER_IDS[mode]` as the value for the CSS `filter` property.
 * For "none", the value is an empty string (no filter).
 */
export const CVD_FILTER_IDS: Record<CvdFilterMode, string> = {
  none: '',
  protanopia: 'url(#cvd-protanopia)',
  deuteranopia: 'url(#cvd-deuteranopia)',
  tritanopia: 'url(#cvd-tritanopia)',
  achromatopsia: 'url(#cvd-achromatopsia)',
};

// ---------------------------------------------------------------------------
// Color matrices (Machado 2009, severity 1.0)
// ---------------------------------------------------------------------------

// Each matrix is a 20-value array for the SVG feColorMatrix "values" attribute.
// Layout: [R→R, G→R, B→R, A→R, bias,  R→G, G→G, B→G, A→G, bias,  ...]

const PROTANOPIA_MATRIX = [
  0.152286, 1.052583, -0.204868, 0, 0,
  0.114503, 0.786281, 0.099216, 0, 0,
  -0.003882, -0.048116, 1.051998, 0, 0,
  0, 0, 0, 1, 0,
].join(' ');

const DEUTERANOPIA_MATRIX = [
  0.367322, 0.860646, -0.227968, 0, 0,
  0.280085, 0.672501, 0.047413, 0, 0,
  -0.011820, 0.042940, 0.968881, 0, 0,
  0, 0, 0, 1, 0,
].join(' ');

const TRITANOPIA_MATRIX = [
  1.255528, -0.076749, -0.178779, 0, 0,
  -0.078411, 0.930809, 0.147602, 0, 0,
  0.004733, 0.691367, 0.303900, 0, 0,
  0, 0, 0, 1, 0,
].join(' ');

// Standard luminance-weighted grayscale (ITU-R BT.709)
const ACHROMATOPSIA_MATRIX = [
  0.2126, 0.7152, 0.0722, 0, 0,
  0.2126, 0.7152, 0.0722, 0, 0,
  0.2126, 0.7152, 0.0722, 0, 0,
  0, 0, 0, 1, 0,
].join(' ');

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a hidden SVG element containing CVD simulation filters.
 * Mount this component once in the app (it renders with zero visual footprint).
 */
export function CVDFilterOverlay(): React.ReactElement {
  return (
    <svg
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
      aria-hidden="true"
    >
      <defs>
        <filter id="cvd-protanopia">
          <feColorMatrix type="matrix" values={PROTANOPIA_MATRIX} />
        </filter>
        <filter id="cvd-deuteranopia">
          <feColorMatrix type="matrix" values={DEUTERANOPIA_MATRIX} />
        </filter>
        <filter id="cvd-tritanopia">
          <feColorMatrix type="matrix" values={TRITANOPIA_MATRIX} />
        </filter>
        <filter id="cvd-achromatopsia">
          <feColorMatrix type="matrix" values={ACHROMATOPSIA_MATRIX} />
        </filter>
      </defs>
    </svg>
  );
}

export default CVDFilterOverlay;
